import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { bookingsAPI, locationsAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, MapPin } from 'lucide-react';
import { getStatusColor, getSportColor } from '../../lib/utils';
import { formatBookingReference, formatPaymentMethod, getPaymentMethodColor, getBookingStatusLabel } from '../../utils/bookingUtils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';
import CustomerHeader from '../../components/CustomerHeader';

export default function BookingsList() {
  const navigate = useNavigate();
  const { user, isGuest, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [locations, setLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getLocations();
      const locsMap = {};
      locs.forEach(loc => { locsMap[loc.id] = loc; });
      setLocations(locsMap);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    if (!user) return;
    try {
      let userBookings;
      if (isGuest && user.phone) {
        userBookings = await bookingsAPI.getGuestBookings(user.phone);
      } else if (user.id) {
        userBookings = await bookingsAPI.getUserBookings(user.id);
      } else {
        userBookings = [];
      }
      setBookings(userBookings || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isGuest]);

  useEffect(() => {
    // Wait for AuthContext to hydrate from localStorage on F5 — otherwise
    // `user` is null on first render and we'd wrongly bounce to "/".
    if (authLoading) return;
    if (!user) {
      const timer = setTimeout(() => navigate('/'), 0);
      return () => clearTimeout(timer);
    }
    loadLocations();
    loadBookings();
  }, [user, authLoading, navigate, loadLocations, loadBookings]);

  const filteredBookings = bookings.filter(b => {
    if (filter === 'all') return true;
    return b.status === filter;
  });

  return (
    <div className="mobile-container min-h-screen pb-bottom-nav">
      <CustomerHeader title="My Bookings" onBack={() => navigate('/home')} />

      <div className="p-6" data-testid="bookings-list">
        <Tabs defaultValue="all" onValueChange={setFilter} className="w-full">
          <TabsList
            className="w-full mb-6 flex gap-1 p-1 bg-slate-100 rounded-full overflow-x-auto"
            style={{ scrollbarWidth: 'none' }}
          >
            <TabsTrigger
              value="all"
              className="flex-shrink-0 rounded-full text-[13px] font-semibold whitespace-nowrap px-2.5 py-2 data-[state=active]:bg-[#0057A8] data-[state=active]:text-white"
            >
              All
            </TabsTrigger>
            <TabsTrigger
              value="AWAITING_PAYMENT"
              className="flex-shrink-0 rounded-full text-[13px] font-semibold whitespace-nowrap px-2.5 py-2 data-[state=active]:bg-[#0057A8] data-[state=active]:text-white"
            >
              Awaiting
            </TabsTrigger>
            <TabsTrigger
              value="CONFIRMED"
              className="flex-shrink-0 rounded-full text-[13px] font-semibold whitespace-nowrap px-2.5 py-2 data-[state=active]:bg-[#0057A8] data-[state=active]:text-white"
            >
              Confirmed
            </TabsTrigger>
            <TabsTrigger
              value="CANCELLED"
              className="flex-shrink-0 rounded-full text-[13px] font-semibold whitespace-nowrap px-2.5 py-2 data-[state=active]:bg-[#0057A8] data-[state=active]:text-white"
            >
              Cancelled
            </TabsTrigger>
          </TabsList>

          <TabsContent value={filter}>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredBookings.length === 0 ? (
              <Card className="p-8 text-center glass-card">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No bookings found</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredBookings.map((booking) => {
                  // Status-aware left-border color (per PART 4 spec)
                  const borderColor = {
                    CONFIRMED: '#16A34A',
                    AWAITING_PAYMENT: '#FF6B00',
                    CANCELLED: '#DC2626',
                    EXPIRED: '#64748B',
                  }[booking.status] || '#0057A8';
                  return (
                    <Card
                      key={booking.id}
                      onClick={() => navigate(`/booking/${booking.id}`)}
                      data-testid="booking-card"
                      className="bba-card hover:border-accent/50 transition-all cursor-pointer court-card"
                      style={{ borderLeft: `4px solid ${borderColor}` }}
                    >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                      <div className="flex items-center gap-2 gap-y-1 flex-wrap mb-1">
                          <span className={`badge ${getStatusColor(booking.status)}`}>
                            {getBookingStatusLabel(booking)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatBookingReference(booking.id)}
                          </span>
                          {booking.payment_method && booking.status === 'CONFIRMED' && (
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              getPaymentMethodColor(booking.payment_method) === 'purple' ? 'bg-purple-100 text-purple-700'
                                : getPaymentMethodColor(booking.payment_method) === 'green' ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {formatPaymentMethod(booking.payment_method)}
                            </span>
                          )}
                        </div>
                        <DateTimeDisplay
                          date={booking.booking_date}
                          timeSlot={booking.time_slot}
                          duration={booking.duration}
                          className="mt-1"
                          testId="bookings-list"
                        />
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-xl">BND {(
                          booking.final_total ?? booking.estimated_total ?? 0
                        ).toFixed(2)}</p>
                      </div>
                    </div>
                    {locations[booking.location_id] && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{locations[booking.location_id].name}</span>
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      {booking.courts_requested.map((court, idx) => (
                        <span key={`${court.sport}-${idx}`} className={`sport-badge ${getSportColor(court.sport)}`}>
                          {court.sport}
                        </span>
                      ))}
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
